import { useCallback, useMemo } from "react";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import Notification from "../../../components/common/notification/Notification";
import BillingCollectionToolbar from "../components/BillingCollectionToolbar";
import BillingConfirmDeleteModal from "../components/BillingConfirmDeleteModal";
import BillingDataTable from "../components/BillingDataTable";
import BillingEntityForm from "../components/BillingEntityForm";
import BillingFormModal from "../components/BillingFormModal";
import BillingPageHeader from "../components/BillingPageHeader";
import BillingStatusToggle from "../components/BillingStatusToggle";
import { useBillingCrudManager } from "../hooks/useBillingCrudManager";
import { createCouponsService } from "../services/couponsService";
import { useBillingApiClient } from "../services/useBillingApiClient";
import { formatDate, formatUsageStats } from "../utils/formatters";
import {
  couponFields,
  couponInitialValues,
  couponValidationSchema,
  formatCouponDiscount,
  mapCouponToFormValues,
  mapCouponToPayload,
} from "./couponsConfig";
import "./CouponsPage.css";

const CouponsPage = () => {
  const apiClient = useBillingApiClient();
  const couponsService = useMemo(() => createCouponsService(apiClient), [apiClient]);

  const loadList = useCallback(
    ({ page, pageSize }) => couponsService.list({ page, pageSize }),
    [couponsService]
  );

  const loadEditableItem = useCallback(
    (itemOrCode) => {
      const couponCode =
        typeof itemOrCode === "object" ? itemOrCode?.coupon_code : itemOrCode;

      return couponsService.getByCode(couponCode);
    },
    [couponsService]
  );

  const manager = useBillingCrudManager({
    entityLabel: "Coupon",
    loadList,
    loadEditableItem,
    createItem: couponsService.create,
    updateItem: couponsService.update,
    deleteItem: couponsService.remove,
    toggleItemStatus: couponsService.toggleStatus,
    searchFields: ["coupon_code", "coupon_type"],
    toFormValues: mapCouponToFormValues,
    toPayload: mapCouponToPayload,
  });

  const columns = useMemo(
    () => [
      {
        header: "S.No.",
        key: "serial",
        render: (_, index) => (manager.page - 1) * manager.pageSize + index + 1,
      },
      {
        header: "Coupon",
        key: "coupon_code",
        cellClassName: "coupons-page__coupon-cell",
        render: (coupon) => (
          <div className="coupons-page__coupon-block">
            <span className="coupons-page__coupon-code">{coupon.coupon_code}</span>
            <span className="coupons-page__coupon-type">
              {coupon.coupon_type || "N/A"}
            </span>
          </div>
        ),
      },
      {
        header: "Discount",
        key: "discount_value",
        render: (coupon) => (
          <span className="coupons-page__discount">
            {formatCouponDiscount(coupon)}
          </span>
        ),
      },
      {
        header: "Trial Bonus",
        key: "extra_trial_days",
        render: (coupon) => `${coupon.extra_trial_days ?? 0} days`,
      },
      {
        header: "Usage",
        key: "usage_limit",
        render: (coupon) => (
          <div className="coupons-page__usage-block">
            <span className="coupons-page__usage-stat">
              {formatUsageStats(coupon.used_count, coupon.usage_limit)}
            </span>
            <span className="coupons-page__usage-caption">
              {coupon.used_count ?? 0} redeemed
            </span>
          </div>
        ),
      },
      {
        header: "Expires",
        key: "expires_at",
        render: (coupon) => formatDate(coupon.expires_at),
      },
    ],
    [manager.page, manager.pageSize]
  );

  return (
    <>
      <Notification />
      <div className="coupons-page">
        <BillingPageHeader
          title="Coupons"
          description="Manage promotional coupon codes with the same reusable billing patterns used for subscription plans, including modal forms, status toggles, and delete confirmation."
          actionLabel="Add Coupon"
          onAction={manager.openCreateForm}
        />

        <BillingCollectionToolbar
          searchValue={manager.searchValue}
          onSearchChange={manager.setSearchValue}
          searchPlaceholder="Search by coupon code or coupon type"
          summary={`${manager.filteredRecords.length} coupons on this page`}
        />

        <BillingDataTable
          columns={columns}
          rows={manager.filteredRecords}
          keyExtractor={(coupon) => coupon.id || coupon.coupon_code}
          isLoading={manager.isLoading}
          errorMessage={manager.errorMessage}
          errorActionLabel="Retry"
          onErrorAction={manager.refresh}
          emptyTitle="No coupons found"
          emptyMessage="Create your first coupon to unlock discount campaigns and trial promotions."
          emptyActionLabel="Create Coupon"
          onEmptyAction={manager.openCreateForm}
          currentPage={manager.page}
          totalPages={manager.totalPages}
          onPageChange={manager.setPage}
          renderRowActions={(coupon) => (
            <div className="coupons-page__actions">
              <div className="coupons-page__action-icons">
                <button
                  type="button"
                  className="coupons-page__icon-button"
                  onClick={() => manager.openEditForm(coupon.coupon_code)}
                  aria-label="Edit coupon"
                  title="Edit"
                >
                  <FiEdit2 size={16} />
                </button>
                <button
                  type="button"
                  className="coupons-page__icon-button coupons-page__icon-button--danger"
                  onClick={() => manager.askToDelete(coupon)}
                  aria-label="Delete coupon"
                  title="Delete"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
              <BillingStatusToggle
                checked={Boolean(coupon.isActive)}
                disabled={manager.isSubmitting}
                onChange={() =>
                  manager.handleStatusToggle(coupon, !Boolean(coupon.isActive))
                }
              />
            </div>
          )}
        />

        <BillingFormModal
          isOpen={manager.formState.isOpen}
          title={manager.formState.mode === "edit" ? "Edit Coupon" : "Add Coupon"}
          description="Set up coupon types, discount values, usage controls, and expiry rules in a reusable billing form."
          onClose={manager.closeForm}
        >
          <BillingEntityForm
            initialValues={manager.formState.values || couponInitialValues}
            validationSchema={couponValidationSchema}
            fields={couponFields}
            onSubmit={manager.handleSubmit}
            isSubmitting={manager.isSubmitting}
            submitLabel={manager.formState.mode === "edit" ? "Update Coupon" : "Create Coupon"}
            onCancel={manager.closeForm}
          />
        </BillingFormModal>

        <BillingConfirmDeleteModal
          isOpen={Boolean(manager.deleteTarget)}
          title="Delete coupon"
          description={`This will permanently remove ${
            manager.deleteTarget?.coupon_code || "this coupon"
          } from the billing system.`}
          isBusy={manager.isSubmitting}
          onClose={manager.cancelDelete}
          onConfirm={manager.confirmDelete}
        />
      </div>
    </>
  );
};

export default CouponsPage;
